import cn from 'classnames';
import React, { useEffect, useRef } from 'react';

import { getImageUrl } from 'editor/src/store/design/selector/getImageElementUrl';
import { GalleryImage } from 'editor/src/store/gallery/types';
import { ArtworkGroup } from 'editor/src/store/gangSheet/util/getArtworkGroups';

import IconBin from 'editor/src/component/Icon/IconBin';
import IconFillSheet from 'editor/src/component/Icon/IconFillSheet';

import QuantityStepper from '../QuantityStepper';

import styles from './index.module.scss';

interface Props {
  group: ArtworkGroup;
  // gallery image of the group's artwork; undefined while the gallery is still loading
  image: GalleryImage | undefined;
  // true when one of the group's canvas elements is selected
  selected: boolean;
  incrementDisabled: boolean;
  onQtyChange: (groupKey: string, qty: number) => void;
  onFillSheet: (groupKey: string) => void;
  onRemove: (groupKey: string) => void;
}

function formatMm(value: number) {
  return String(Math.round(value * 10) / 10);
}

function ArtworkRow({ group, image, selected, incrementDisabled, onQtyChange, onFillSheet, onRemove }: Props) {
  const name = image?.name ?? '';
  const rowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selected) {
      rowRef.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [selected]);

  return (
    <div ref={rowRef} className={cn(styles.ArtworkRow, 'cy-gang-sheet-artwork-row', { [styles.selected]: selected })}>
      {image && <img className={styles.thumbnail} src={getImageUrl(image, true)} alt={name} draggable={false} />}
      <div className={styles.info}>
        <div className={styles.name} title={name}>
          {name}
        </div>
        <div className={styles.details}>{`${formatMm(group.widthMm)} × ${formatMm(group.heightMm)} mm`}</div>
      </div>
      <div className={styles.rowActions}>
        <QuantityStepper
          value={group.elements.length}
          min={0}
          onChange={(qty) => onQtyChange(group.key, qty)}
          incrementDisabled={incrementDisabled}
        />
        <div className={styles.iconButtons}>
          {/* TODO: Lokalino (title, aria-label) */}
          <button
            type="button"
            className={cn(styles.iconButton, 'cy-gang-sheet-fill-button')}
            onClick={() => onFillSheet(group.key)}
            title="Fill sheet with this artwork"
            aria-label="Fill sheet with this artwork"
          >
            <IconFillSheet className={styles.buttonIcon} />
          </button>
          {/* TODO: Lokalino (title, aria-label) */}
          <button
            type="button"
            className={cn(styles.iconButton, 'cy-gang-sheet-remove-button')}
            onClick={() => onRemove(group.key)}
            title="Remove this artwork from the sheet"
            aria-label="Remove this artwork from the sheet"
          >
            <IconBin className={styles.buttonIcon} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default React.memo(ArtworkRow);
